import { DateTime } from 'luxon';

import { IShiftsFilters, ReviewMode } from './types';

const REVIEW_MODES = new Set<ReviewMode>(['normal', 'review:team', 'review:all']);

const parseDateParam = (value: string | null): Date => {
  if (!value) {
    return new Date();
  }
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toJSDate() : new Date();
};

const parseIdParam = (value: string | null): number | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

export const parseDateInput = (value: string): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toJSDate() : undefined;
};

export const formatFilterDateValue = (date: Date | undefined): string => {
  if (!date) {
    return '';
  }
  const parsed = DateTime.fromJSDate(date);
  return parsed.isValid ? parsed.toFormat('yyyy-MM-dd') : '';
};

export const cleanupFilters = (filters: IShiftsFilters): Record<string, string | number> => {
  const results: Record<string, string | number> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value instanceof Date) {
      results[key] = DateTime.fromJSDate(value).toFormat('yyyy-MM-dd');
      return;
    }
    if (value !== undefined && value !== null && value !== '') {
      results[key] = value as string | number;
    }
  });

  return results;
};

export const parseFiltersFromSearch = (search: string): IShiftsFilters => {
  const params = new URLSearchParams(search);
  const filters: IShiftsFilters = {};

  const teamID = parseIdParam(params.get('team_id'));
  if (teamID !== undefined) {
    filters.team_id = teamID;
  }

  const regionID = parseIdParam(params.get('region_id'));
  if (regionID !== undefined) {
    filters.region_id = regionID;
  }

  filters.from = parseDateParam(params.get('from'));
  filters.till = parseDateParam(params.get('till'));

  const query = params.get('query');
  if (query) {
    filters.query = query;
  }

  const timeClockPlus = params.get('time_clock_plus');
  if (timeClockPlus) {
    filters.time_clock_plus = timeClockPlus;
  }

  const mode = params.get('mode');
  if (mode && REVIEW_MODES.has(mode as ReviewMode)) {
    filters.mode = mode;
  }

  return filters;
};

export const syncFiltersToUrl = (filters: IShiftsFilters, mode: string | undefined) => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams();

  const cleaned = cleanupFilters({ ...filters, mode });
  Object.entries(cleaned).forEach(([key, value]) => {
    searchParams.set(key, String(value));
  });

  url.search = searchParams.toString();
  window.history.replaceState({}, '', url.toString());
};

export const restrictFromAndTillFilters = (filters: IShiftsFilters, attribute?: 'from' | 'till'): IShiftsFilters => {
  const from = DateTime.fromJSDate(filters.from ?? new Date());
  const till = DateTime.fromJSDate(filters.till ?? new Date());
  const next = { ...filters };

  if (till < from) {
    switch (attribute) {
      case 'from':
        next.till = next.from;
        break;
      case 'till':
        next.from = next.till;
        break;
      default:
        next.from = new Date();
        next.till = new Date();
        break;
    }
  } else if (till.diff(from, 'days').days > 7) {
    switch (attribute) {
      case 'from':
        next.till = from.plus({ days: 7 }).toJSDate();
        break;
      case 'till':
        next.from = till.minus({ days: 7 }).toJSDate();
        break;
      default:
        next.from = new Date();
        next.till = new Date();
        break;
    }
  }

  return next;
};

export const dateInputBounds = () => {
  const now = DateTime.now();
  return {
    min: now.minus({ years: 2 }).toFormat('yyyy-MM-dd'),
    max: now.plus({ years: 2 }).toFormat('yyyy-MM-dd'),
  };
};
